import React, { useState, useEffect } from 'react';
import { referralAPI, stripeAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';
import CoachSidebar from '../components/Dashboard/CoachSidebar';
import EditAvatarModal from '../components/Modals/EditAvatarModal';
import EditPromptModal from '../components/Modals/EditPromptModal';

const Referrals = () => {
  const { user } = useAuth();
  const [hasPathASubscription, setHasPathASubscription] = useState(false);

  const [coachCode, setCoachCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Approval / Configuration Modal State
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedClientToApprove, setSelectedClientToApprove] = useState(null);
  const [approvalPath, setApprovalPath] = useState('A');
  const [approvalPrice, setApprovalPrice] = useState(120);
  const [isApproving, setIsApproving] = useState(false);
  
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);

  const fetchReferrals = async () => {
    try {
      const [refRes, statsRes] = await Promise.all([
        referralAPI.getMyReferrals(),
        stripeAPI.getFinancesStats()
      ]);
      setCoachCode(refRes.data.coachCode || '');
      setReferrals(refRes.data.referrals || []);
      setHasPathASubscription(statsRes.data?.hasPathASubscription || false);
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(coachCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/register?ref=${coachCode}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleGenerateCode = async () => {
    setGenerating(true);
    try {
      const response = await referralAPI.generateCode();
      setCoachCode(response.data.coachCode);
    } catch (error) {
      console.error('Error generating code:', error);
      alert('Failed to generate referral code. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveClick = (referral) => {
    if (!hasPathASubscription) {
      alert("You need an active Path A subscription to approve clients or configure their access plans. Redirecting you to the Finances page...");
      window.location.href = '/coach/finances';
      return;
    }
    setSelectedClientToApprove(referral.clientId);
    // Pre-populate with current path (or default to A if active, else B)
    const defaultPath = referral.path || 'A';
    setApprovalPath(defaultPath);
    setApprovalPrice(referral.price || 120);
    setApprovalModalOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedClientToApprove) return;
    if (!hasPathASubscription) {
      alert("You need an active Path A subscription to approve clients.");
      return;
    }
    if (approvalPath === 'B' && approvalPrice < 120) {
      alert("Minimum price for Path B is $120.");
      return;
    }

    try {
      setIsApproving(true);
      await stripeAPI.approveClient(selectedClientToApprove, approvalPath, approvalPath === 'B' ? approvalPrice : 0);
      setReferrals(prev =>
        prev.map(r => r.clientId === selectedClientToApprove ? { ...r, isApproved: true, path: approvalPath, price: approvalPath === 'B' ? approvalPrice : 0 } : r)
      );
      setApprovalModalOpen(false);
      setSelectedClientToApprove(null);
    } catch (error) {
      console.error('Error configuring plan:', error);
      alert(error.response?.data?.error || 'Failed to configure plan.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleRemove = async (clientId) => {
    if (!window.confirm('Are you sure you want to remove this client from your referrals? They will lose access.')) return;
    try {
      await stripeAPI.revokeClient(clientId);
      setReferrals(prev => prev.filter(r => r.clientId !== clientId));
    } catch (error) {
      console.error('Error removing client:', error);
      alert('Failed to remove client.');
    }
  };

  if (loading) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-offwhite group/design-root overflow-x-hidden font-display">
        <div className="layout-container flex h-full grow flex-col">
          <div className="flex justify-center items-center h-64 w-full flex-1">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-tangerine"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      
      <EditAvatarModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} currentAvatar="" />
      <EditPromptModal isOpen={isPromptModalOpen} onClose={() => setIsPromptModalOpen(false)} />

      {approvalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[var(--surface-white)] rounded-3xl p-6 w-[90%] max-w-md shadow-2xl border border-[var(--border-light)] text-[var(--text-primary)]">
            <h2 className="text-xl font-bold mb-1 text-[var(--text-primary)]">
              {referrals.find(r => r.clientId === selectedClientToApprove)?.isApproved ? 'Configure Plan' : 'Approve Client & Set Plan'}
            </h2>
            <p className="text-xs text-[var(--text-secondary)] mb-5">
              Choose how <span className="font-bold text-[var(--text-primary)]">{referrals.find(r => r.clientId === selectedClientToApprove)?.name}</span> will access the platform.
            </p>
            
            <div className="space-y-4 mb-6">
              {/* Path A Option */}
              <label 
                className={`block border p-4 rounded-xl cursor-pointer transition-colors ${
                  !hasPathASubscription 
                    ? 'opacity-60 border-dashed border-[var(--border-light)] cursor-not-allowed bg-[var(--off-white)]/30' 
                    : 'border-[var(--border-light)] hover:bg-[var(--off-white)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input 
                      type="radio" 
                      name="path" 
                      value="A" 
                      disabled={!hasPathASubscription}
                      checked={approvalPath === 'A'} 
                      onChange={() => setApprovalPath('A')} 
                      className="w-4 h-4 text-tangerine focus:ring-tangerine disabled:opacity-50" 
                    />
                    <span className={`font-bold ${!hasPathASubscription ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'}`}>
                      Path A: You pay for the client
                    </span>
                  </div>
                  {!hasPathASubscription && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded-md border border-orange-500/20">
                      Locked
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-2 ml-7">
                  Requires an available seat on your Path A subscription. Client gets free access.
                </p>
                {!hasPathASubscription && (
                  <p className="text-[11px] text-orange-500 font-medium mt-1 ml-7 flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs">info</span>
                    Requires an active subscription. Subscribe under Finances page.
                  </p>
                )}
              </label>

              {/* Path B Option */}
              <label className="block border border-[var(--border-light)] p-4 rounded-xl cursor-pointer hover:bg-[var(--off-white)] transition-colors">
                <div className="flex items-center space-x-3">
                  <input 
                    type="radio" 
                    name="path" 
                    value="B" 
                    checked={approvalPath === 'B'} 
                    onChange={() => setApprovalPath('B')} 
                    className="w-4 h-4 text-tangerine focus:ring-tangerine" 
                  />
                  <span className="font-bold text-[var(--text-primary)]">Path B: Client pays directly</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-2 ml-7">
                  Client enters their card details. You set their monthly price.
                </p>
              </label>
            </div>

            {approvalPath === 'B' && (
              <div className="mb-6 animate-in fade-in duration-200">
                <label className="block text-sm font-bold mb-2 text-[var(--text-primary)]">Set Monthly Price ($)</label>
                <input 
                  type="number" 
                  min="120"
                  value={approvalPrice} 
                  onChange={(e) => setApprovalPrice(Number(e.target.value))}
                  className="w-full p-3 border border-[var(--border-light)] bg-[var(--off-white)] text-[var(--text-primary)] rounded-xl focus:outline-none focus:ring-2 focus:ring-tangerine transition-all"
                />
                <p className="text-xs text-[var(--text-secondary)] mt-1">Minimum price: $120.</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setApprovalModalOpen(false)}
                className="px-4 py-2 font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmApprove}
                disabled={isApproving}
                className="px-6 py-2 bg-tangerine text-white font-bold rounded-xl shadow disabled:opacity-50 hover:bg-orange-600 transition-colors text-sm flex items-center justify-center gap-2"
              >
                {isApproving ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-8">
                Referrals
              </h1>

              {/* Referral Code Card */}
              <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl p-6 md:p-8 mb-8">
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">Your Referral Code</h2>
                <p className="text-[var(--text-secondary)] text-sm mb-4">
                  Share this code or link with clients so they can sign up under your account.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 flex items-center bg-[var(--off-white)] border border-[var(--border-light)] rounded-xl px-4 py-3">
                    <span className={`text-lg font-mono font-bold tracking-wider flex-1 ${coachCode ? 'text-tangerine' : 'text-[var(--text-secondary)]/60+ italic'}`}>
                      {coachCode || 'No code generated'}
                    </span>
                    {coachCode && (
                      <button
                        onClick={handleCopyCode}
                        className="ml-3 text-sm font-bold text-[var(--text-secondary)] hover:text-tangerine transition-colors"
                      >
                        {copied ? '✓ Copied!' : 'Copy Code'}
                      </button>
                    )}
                  </div>
                  
                  {coachCode ? (
                    <button
                      onClick={handleCopyLink}
                      className="bg-tangerine hover:bg-tangerine/90 text-white font-bold shadow-md shadow-tangerine/20 transition-all hover:-translate-y-0.5 px-5 py-3 rounded-xl text-sm whitespace-nowrap"
                    >
                      {copiedLink ? '✓ Link Copied!' : '🔗 Copy Invite Link'}
                    </button>
                  ) : (
                    <button
                      onClick={handleGenerateCode}
                      disabled={generating}
                      className="bg-tangerine hover:bg-tangerine/90 text-white font-bold shadow-md shadow-tangerine/20 transition-all hover:-translate-y-0.5 px-6 py-3 rounded-xl text-sm whitespace-nowrap flex items-center justify-center min-w-[180px]"
                    >
                      {generating ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      ) : (
                        <span className="material-symbols-outlined text-lg mr-2">magic_button</span>
                      )}
                      {generating ? 'Generating...' : 'Generate Code'}
                    </button>
                  )}
                </div>
              </div>

              {/* Referred Clients Table */}
              <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-[var(--text-primary)]">Referred Clients</h2>
                  <span className="text-sm text-[var(--text-secondary)] bg-[var(--off-white)] px-3 py-1 rounded-full font-medium">
                    {referrals.length} {referrals.length === 1 ? 'client' : 'clients'}
                  </span>
                </div>

                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-[var(--off-white)] rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">👥</span>
                    </div>
                    <p className="text-[var(--text-secondary)] text-sm">
                      No clients yet. Share your referral code to get started!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((referral) => (
                      <div
                        key={referral.id}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl border transition-all ${
                          referral.isApproved
                            ? 'bg-green-500/10 border-green-500/20'
                            : 'bg-yellow-500/10 border-yellow-500/20'
                        }`}
                      >
                        <div className="flex items-center space-x-4 mb-3 sm:mb-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            referral.isApproved ? 'bg-green-500' : 'bg-yellow-500'
                          }`}>
                            {referral.name ? referral.name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div>
                            <p className="font-bold text-[var(--text-primary)]">{referral.name}</p>
                            <p className="text-sm text-[var(--text-secondary)]">{referral.email}</p>
                            {referral.city && (
                              <p className="text-xs text-[var(--text-secondary)]/60+">{referral.city}</p>
                            )}
                            {referral.isApproved && referral.path && (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-xs font-bold text-tangerine bg-orange-50 px-2 py-0.5 rounded">
                                  Path {referral.path}
                                </span>
                                {referral.path === 'B' && referral.price && (
                                  <span className="text-xs text-gray-500 font-medium">
                                    ${referral.price}/mo
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                            referral.status === 'canceled'
                              ? 'text-red-500 bg-red-100'
                              : referral.isApproved
                                ? 'text-green-400 bg-green-500/10'
                                : 'text-yellow-400 bg-yellow-500/10'
                          }`}>
                            {referral.status === 'canceled' ? 'Canceled' : referral.isApproved ? '✓ Approved' : '⏳ Pending'}
                          </span>

                          {(!referral.isApproved || !referral.path) && referral.status !== 'canceled' && (
                            <button
                              onClick={() => handleApproveClick(referral)}
                              className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-all hover:-translate-y-0.5 shadow-sm"
                            >
                              {!referral.isApproved ? 'Approve' : 'Configure Path'}
                            </button>
                          )}

                          {referral.isApproved && referral.path && referral.status !== 'canceled' && (
                            <button
                              onClick={() => handleApproveClick(referral)}
                              className="bg-[var(--off-white)] border border-[var(--border-light)] hover:bg-gray-100 text-[var(--text-secondary)] text-sm font-bold px-4 py-2 rounded-xl transition-all shadow-sm"
                            >
                              Change Plan
                            </button>
                          )}

                          <button
                            onClick={() => handleRemove(referral.clientId)}
                            className="bg-[var(--surface-white)] border border-[var(--border-light)] hover:border-red-400 hover:text-red-500 text-[var(--text-secondary)] text-sm font-bold px-4 py-2 rounded-xl transition-all shadow-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </main>

            <CoachSidebar 
              activeTab=""
              onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
              onOpenPromptModal={() => setIsPromptModalOpen(true)}
              onOpenSettingsModal={() => { window.location.href = '/coach/settings' }}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
